import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { actionCreators } from '@/store/MyTickets'
import { Form, FormGroup, Col, Label, ModalBody, ModalFooter, Input } from 'reactstrap'
import Resx from '@/components/Resx'
import { DropDownList } from '@/components/dropDownList'
import context from '@/context'
import Icon from '@/components/Icon'
import { defaultChangeHandler, FileAttachments } from '@/components/fileAttachments'
import { createInputs } from './ticketTypes'
import { ValidationBoundary, Validator, ValidationState } from '@/components/validation'
import { ServerErrorViewer } from '@/components/validation/serverErrorViewer'
import { BasicHeader } from '@/dialogs/common/basicHeader'
import getComponentKey from '@/utils/getComponentKey'
import { useLocalization } from '@/hooks/localization'
import { useDispatch } from '@/hooks/dispatch'

function CreateNewTicketDialog (props) {
  const { dialogUid } = props

  const [state, setState] = useState({
    parent: null,
    workflowDefinition: null,
    name: '',
    description: '',
    attachments: [],
    inputData: null,
    validationComponentKey: getComponentKey()
  })
  const { error, creating } = useSelector(state => {
    return {
      error: state.myTickets.error,
      creating: state.myTickets.creating
    }
  })
  const { locale } = useLocalization()

  const [createTicket] = useDispatch([actionCreators.createTicket])

  const saveChanges = () => {
    const ticket = {
      parentId: (state.parent ? state.parent.id : null),
      name: state.name,
      description: state.description,
      workflowDefinitionCode: state.workflowDefinition,
      attachments: state.attachments.map(x => x.id),
      inputData: state.inputData ? JSON.stringify(state.inputData) : null
    }
    createTicket(ticket, dialogUid)
  }

  const onAttachmentChange = useCallback((e) => {
    const attachments = defaultChangeHandler(e, state.attachments || [])
    if (attachments !== state.attachments) {
      setState({ ...state, attachments: attachments })
    }
  }, [state])

  const onChangeType = useCallback(({ value }) => {
    setState({ ...state, workflowDefinition: value, validationComponentKey: getComponentKey(), inputData: null })
  }, [state])

  const Inputs = createInputs(state.workflowDefinition)
  return (
    <ValidationBoundary serverError={error}>
      <BasicHeader title='Tickets.Title' className='modal-header--purple' {...props} />
      <ModalBody>
        <Form>
          <FormGroup row>
            <Label sm='2' className='label'>
              <Resx name='Common.Name' />
            </Label>
            <Col sm='10'>
              <Validator name='name' value={state.name} required maxLength={100}>
                <Input id='name' className='form-control' value={state.name}
                  onChange={e => setState({ ...state, name: e.target.value })} />
              </Validator>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm='2' className='label'>
              <Resx name='Common.Description' />
            </Label>
            <Col sm='10'>
              <Validator name='description' value={state.description} required>
                <Input type='textarea'
                  rows='4'
                  onChange={e => setState({ ...state, description: e.target.value })}
                  value={state.description} />
              </Validator>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm='2' className='label'><Resx name='Tickets.ParentTicket' /></Label>
            <Col sm='10'>
              <DropDownList
                dataSource={context.api.ticketService.lookup}
                value={state.parent}
                textField='name'
                onChange={({ value }) => setState({ ...state, parent: value })}
                allowClear
                noSorting
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm='2' className='label'>
              <Resx name='Tickets.Type' />
            </Label>
            <Col sm='10'>
              <Validator name='workflowDefinition' value={state.workflowDefinition} required>
                <DropDownList
                  value={state.workflowDefinition}
                  dataSource={context.api.workflowDefinitionService.getManualWorkflows}
                  textField={locale === 'en' ? 'latinName' : 'name'}
                  valueField='code'
                  onChange={onChangeType} />
              </Validator>
            </Col>
          </FormGroup>
          <Inputs value={state.inputData || {}} onChange={inputData => setState({ ...state, inputData })} />
          <FormGroup row>
            <Label sm='2' className='label'><Resx name='Common.Attachments' /></Label>
            <Col sm='10'>
              <Validator name='attachments'>
                <FileAttachments attachments={state.attachments || []} onChange={onAttachmentChange} />
              </Validator>
            </Col>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <ValidationState render={({ isValid, serverError }) =>
          <React.Fragment>
            <ServerErrorViewer showErrorMessage error={serverError} />
            <button className='btn btn-success ml-auto' onClick={saveChanges} disabled={creating || !isValid}>
              <Icon name={creating ? 'bx-loader bx-spin' : 'save'} />
              <span><Resx name='Common.SaveAndClose' /></span>
            </button>
          </React.Fragment>
        } />
      </ModalFooter>
    </ValidationBoundary>
  )
}

export default {
  modalProps: {
    className: 'modal-dialog--lg'
  },
  component: CreateNewTicketDialog
}



// WEBPACK FOOTER //
// ./src/dialogs/newTicket.js